// API 配置工具模块
// 负责加载/保存用户 API 偏好（base URL 和 key），构建请求头，并解析端点 URL 以适配配置的 base。
import { clearStoredUser, getStoredUserId } from '@/client/auth/userStorage';

export interface ApiPrefs {
  apiBase: string;
  apiKey: string;
}

export const DEFAULT_API_BASE = 'https://www.wyjh.top';
export const API_KEY_PREFIX = 'sk-';
const PREFS_STORAGE_KEY = 'iarts_prefs';
const SYSTEM_TOKEN_STORAGE_KEY = 'iarts_system_token';

const memoryPrefs: ApiPrefs = { apiBase: DEFAULT_API_BASE, apiKey: '' };
let memorySystemToken = '';

type PrefsStoragePayload = {
  apiKey?: string;
  apiBase?: string;
  username?: string;
  autoSaveHistory?: boolean;
  [key: string]: unknown;
};

const getSafeStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};


export const stripApiKeyPrefix = (apiKey: string): string => {
  const trimmed = typeof apiKey === 'string' ? apiKey.trim() : '';
  if (!trimmed) {
    return '';
  }
  const lower = trimmed.toLowerCase();
  if (!lower.startsWith(API_KEY_PREFIX)) {
    return trimmed;
  }
  return trimmed.slice(API_KEY_PREFIX.length);
};

export const ensureApiKeyPrefix = (apiKey: string): string => {
  const base = stripApiKeyPrefix(apiKey);
  if (!base) {
    return '';
  }
  return `${API_KEY_PREFIX}${base}`;
};

export const getStoredSystemToken = (): string => {
  if (memorySystemToken) {
    return memorySystemToken;
  }
  const storage = getSafeStorage();
  if (!storage) {
    return memorySystemToken;
  }
  try {
    const raw = storage.getItem(SYSTEM_TOKEN_STORAGE_KEY);
    if (typeof raw === 'string') {
      memorySystemToken = stripApiKeyPrefix(raw);
    } else {
      memorySystemToken = '';
    }
  } catch (error) {
    console.warn('getStoredSystemToken: fail to read', error);
    memorySystemToken = '';
  }
  return memorySystemToken;
};

export const setStoredSystemToken = (token: string): void => {
  const normalized = stripApiKeyPrefix(typeof token === 'string' ? token : '');
  memorySystemToken = normalized;
  const storage = getSafeStorage();
  if (!storage) {
    return;
  }
  try {
    if (normalized) {
      storage.setItem(SYSTEM_TOKEN_STORAGE_KEY, normalized);
    } else {
      storage.removeItem(SYSTEM_TOKEN_STORAGE_KEY);
    }
  } catch (error) {
    console.warn('setStoredSystemToken: failed to persist system token', error);
  }
};

export const clearStoredSystemToken = (): void => {
  setStoredSystemToken('');
};

export const handleInvalidToken = (): boolean => {
  try {
    clearStoredSystemToken();
  } catch (error) {
    console.warn('handleInvalidToken: failed to clear system token', error);
  }

  try {
    clearStoredUser();
  } catch (error) {
    console.warn('handleInvalidToken: failed to clear user', error);
  }

  try {
    setStoredApiKey('');
  } catch (error) {
    console.warn('handleInvalidToken: failed to clear api key', error);
  }

  return true;
};

export const loadApiPrefs = (): ApiPrefs => {
  const storage = getSafeStorage();
  if (!storage) {
    return { ...memoryPrefs };
  }
  try {
    const raw = storage.getItem(PREFS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as PrefsStoragePayload;
      const storedKey = typeof parsed.apiKey === 'string' ? parsed.apiKey : '';
      const apiKey = stripApiKeyPrefix(storedKey);
      memoryPrefs.apiBase = DEFAULT_API_BASE;
      memoryPrefs.apiKey = apiKey;
      return { apiBase: DEFAULT_API_BASE, apiKey };
    }
  } catch (error) {
    console.warn('loadApiPrefs: fail to parse prefs', error);
  }
  memoryPrefs.apiBase = DEFAULT_API_BASE;
  memoryPrefs.apiKey = '';
  return { apiBase: DEFAULT_API_BASE, apiKey: '' };
};

/**
 * 判断 API 配置是否完整（base URL 和 key 均非空且已修剪空白）
 * @returns {boolean} true 表示已配置，否则 false
 */
export const isApiConfigured = (): boolean => {
  const prefs = loadApiPrefs();
  return !!(prefs.apiBase.trim() && prefs.apiKey.trim());
};

export const resolveApiEndpoint = (endpoint: string): string => {
  if (!endpoint) return endpoint;
  const trimmed = endpoint.trim();
  const prefs = loadApiPrefs();
  const base = prefs.apiBase ? prefs.apiBase.trim().replace(/\/$/, '') : '';

  // If endpoint is an absolute URL that points to an internal /fal-ai/... route,
  // convert it to use the configured apiBase + pathname so we don't call external queue hosts.
  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const u = new URL(trimmed);
      if (u.pathname && u.pathname.startsWith('/fal-ai/')) {
        const pathWithQuery = u.pathname + u.search + u.hash;
        return base ? base + pathWithQuery : pathWithQuery;
      }
    } catch (error) {
      console.warn('resolveApiEndpoint: invalid URL detected', error);
    }
    return trimmed;
  }

  if (trimmed.startsWith('/')) {
    return base ? base + trimmed : trimmed;
  }
  // relative without leading slash
  return base ? `${base}/${trimmed.replace(/^\//, '')}` : trimmed;
};

export const buildHeaders = (extra?: Record<string, string>): Record<string, string> => {
  const prefs = loadApiPrefs();
  const base: Record<string, string> = { ...(extra || {}) };
  const hasAuthorization = Object.keys(base).some((key) => key.toLowerCase() === 'authorization');
  if (!hasAuthorization) {
    const normalizedToken = ensureApiKeyPrefix(prefs.apiKey);
    if (normalizedToken) {
      base.Authorization = `Bearer ${normalizedToken}`;
    }
  }
  const hasNewApiUser = Object.keys(base).some((key) => key.toLowerCase() === 'new-api-user');
  if (!hasNewApiUser) {
    const userId = getStoredUserId();
    base['new-api-user'] = userId ?? '-1';
  }
  return base;
};

const persistPrefs = (prefs: PrefsStoragePayload): void => {
  const storage = getSafeStorage();
  if (!storage) {
    return;
  }
  try {
    storage.setItem(PREFS_STORAGE_KEY, JSON.stringify(prefs));
  } catch (error) {
    console.warn('persistPrefs: failed to persist api prefs', error);
  }
};

const loadRawPrefs = (): PrefsStoragePayload => {
  const storage = getSafeStorage();
  if (!storage) {
    return { ...memoryPrefs };
  }
  try {
    const raw = storage.getItem(PREFS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as PrefsStoragePayload;
      return parsed && typeof parsed === 'object' ? parsed : {};
    }
  } catch (error) {
    console.warn('loadRawPrefs: fail to parse prefs', error);
  }
  return {};
};

export const setStoredApiKey = (apiKey: string): void => {
  const normalized = stripApiKeyPrefix(apiKey);
  memoryPrefs.apiBase = DEFAULT_API_BASE;
  memoryPrefs.apiKey = normalized;
  const rawPrefs = loadRawPrefs();
  const nextPrefs: PrefsStoragePayload = {
    ...rawPrefs,
    apiBase: DEFAULT_API_BASE,
    apiKey: ensureApiKeyPrefix(normalized),
  };
  persistPrefs(nextPrefs);
};
